import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import backend from '@api/backend'
import {
  extractFlow,
  formatCurrency,
  roundToTwo,
} from '@common/utils/helperFunctions'
import {
  Aggregator,
  deviceWaterUsage,
  HourlyUsage,
  UsageQuery,
  WaterUsage,
  WaterUsageCosts,
  WaterUsageQuery,
  WaterUsageQueryMultipleDevices,
  WaterUsageStats,
} from './model/waterUsage.model'
import Device from '@data/device/model/device'
import { notifyError } from '@common/utils/notify'
import camelcaseKeys from 'camelcase-keys'
import {
  AggregatedUsageByDevice,
  AggregatedUsageResponse,
  AggUsageQuery,
  MonthlyLeakByDevice,
  MonthlyLeakQuery,
  MonthlyLeakResponse,
} from './model/usageStats.model'
import {
  format,
  set,
} from 'date-fns'
import { apiDateFormat } from '@common/utils/constants'

// Usage is ingested hourly at most, so anything shorter refetches data that cannot have
// changed. `gcTime` has to move with it: it defaults to 5 minutes, and once the cache entry
// is collected a remount refetches no matter how long the data was still considered fresh —
// which is most of what navigating away from a usage view and back again does.
const USAGE_STALE_TIME = 1000 * 60 * 60 // 1 hour
const USAGE_GC_TIME = 1000 * 60 * 60 // 1 hour

// `isFetchEnabled` lets a consumer that cannot show the data yet hold the request back.
// It defaults to true so consumers that render usage immediately are unaffected; the
// shared cache key means whichever consumer asks first serves the rest.
export const useUsageStats = (params: UsageQuery, isFetchEnabled: boolean = true) => {
  return useQuery({
    queryKey: ['water-usage-stats', params.uom, params.dUuids ?? null],
    queryFn: () => getUsageStats(params),
    enabled: !!params.uom && isFetchEnabled,
    // Changing the scoped device list changes the key; without this the chart blanks on
    // every selection change instead of holding the previous devices until data lands.
    placeholderData: keepPreviousData,
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
  })
}

// Separate from the stats above because only the Current Water Usage view reads hourly
// data, and only on its Hourly tab. Merged, every stats consumer paid for this request.
export const useHourlyUsage = (params: UsageQuery, isFetchEnabled: boolean = true) => {
  return useQuery({
    queryKey: ['water-usage-hourly', params.uom, params.dateType, params.dUuids ?? null],
    queryFn: () => getHourlyUsage(params),
    enabled: !!params.uom && isFetchEnabled,
    // Changing the scoped device list changes the key; without this the chart blanks on
    // every selection change instead of holding the previous devices until data lands.
    placeholderData: keepPreviousData,
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
  })
}

export const useWaterUsageCost = (uom: string) => {
  return useQuery({
    queryKey: ['water-usage-costs', uom],
    queryFn: () => getWaterUsageCosts(uom),
    enabled: !!uom,
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
  })
}

export const useWaterCostFilter = (
  selectedDevices: Device[],
  waterUsageCosts: { [key: string]: WaterUsageCosts } | undefined,
) => {
  return useQuery({
    queryKey: ['costs-filter', selectedDevices, waterUsageCosts],
    queryFn: () => filterWaterUsageCosts(selectedDevices, waterUsageCosts),
  })
}

export const useWaterStatsFilter = (
  selectedDevices: Device[],
  waterUsageStats: { [key: string]: WaterUsageStats } | undefined,
) => {
  return useQuery({
    queryKey: ['stats-filter', selectedDevices, waterUsageStats],
    queryFn: () => filterWaterUsageStats(selectedDevices, waterUsageStats),
  })
}

export const useDevicesWaterUsage = (
  params: WaterUsageQueryMultipleDevices,
  isFetchEnabled: boolean = false,
) => {
  return useQuery({
    queryKey: getDevicesWaterUsageQueryKey(params),
    queryFn: () => getDevicesWaterUsage(params),
    enabled: isFetchEnabled,
    placeholderData: keepPreviousData,
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
  })
}

export const getDevicesWaterUsageQueryKey = (params: WaterUsageQueryMultipleDevices) => [
  'devices-water-usage',
  {
    deviceIds: [...params.deviceIds],
    startDate: params.startDate,
    endDate: params.endDate,
    uom: params.uom,
    idType: params.idType,
    dateType: params.dateType,
  },
] as const

export const useRefreshDeviceWaterUsage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, params }: { deviceId: string; params: WaterUsageQueryMultipleDevices }) =>
      getLatestDeviceWaterUsage(deviceId, params),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(getDevicesWaterUsageQueryKey(variables.params), data)
      }
    },
  })
}

export const useAlertWaterUsage = (params: WaterUsageQuery) => {
  return useQuery({
    queryKey: ['alert-water-usage', params.deviceId, params.startDate, params.endDate, params.uom],
    queryFn: () => getAlertWaterUsage(params),
    enabled: false,
  })
}

export const useAggregatedUsage = (params: AggUsageQuery) => {
  return useQuery({
    queryKey: ['aggregated-water-usage'],
    queryFn: () => getAggregatedUsage(params),
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
    enabled: !!params.uom,
  })
}

export const useMonthlyLeakStats = (params: MonthlyLeakQuery) => {
  return useQuery({
    queryKey: ['monthly-leak-stats', params.uom],
    queryFn: () => getMonthlyLeakStats(params),
    enabled: !!params.uom,
    staleTime: USAGE_STALE_TIME,
    gcTime: USAGE_GC_TIME,
  })
}

export const useMonthlyLeakStatsFilter = (
  devices: Device[],
  monthlyLeakStats: MonthlyLeakByDevice | undefined,
) => {
  return useQuery({
    queryKey: ['monthly-leak-stats-filter', devices],
    queryFn: () => filterMonthlyLeakStats(devices, monthlyLeakStats),
    enabled: !!monthlyLeakStats,
  })
}

export async function getHourlyUsage(
  params: UsageQuery,
): Promise<{ [key: string]: HourlyUsage[] }> {
  const now = new Date()
  const response = await backend.get(`/water_usage`, {
    params: {
      startDate: params.startDate || format(set(now, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      }), apiDateFormat),
      endDate: params.endDate || format(now, apiDateFormat),
      uom: params.uom,
      dateType: params.dateType,
      ...(params.dUuids?.length ? { d_uuids: params.dUuids } : {}),
    },
    // As on stats_cached: the API reads `d_uuids` as a repeated key, and Axios' default
    // `d_uuids[]=` form is ignored, silently returning the fleet.
    paramsSerializer: { indexes: null },
  })

  // Keyed by `d_uuid`, matching `getUsageStats`, so a caller joins both halves with the
  // same stable id off the device.
  return (response?.data?.usage_data ?? []).reduce(
    (acc: { [key: string]: HourlyUsage[] }, h: any) => {
      if (!acc[h.d_uuid]) acc[h.d_uuid] = Array(24)
      acc[h.d_uuid][h.hour] = {
        date: `${h.date}`,
        utc_date: `${h.utc_date}`,
        value: h.flow,
      }
      return acc
    },
    {},
  )
}

export async function getUsageStats(
  params: UsageQuery,
): Promise<{ [key: string]: WaterUsageStats }> {
  const response = await backend.get(`/water_usage/stats_cached`, {
    params: {
      uom: params.uom,
      ...(params.dUuids?.length ? { d_uuids: params.dUuids } : {}),
    },
    // The API reads `d_uuids` as a repeated key. Axios' default would send `d_uuids[]=...`,
    // which it ignores — leaving the request silently unscoped and returning the fleet.
    paramsSerializer: { indexes: null },
  })

  const statsArr = response?.data?.usage_data ?? []

  return statsArr.reduce((agg: { [key: string]: WaterUsageStats }, stats: any) => {
    const dUuid = stats.d_uuid + ''
    agg[dUuid] = {
      deviceId: stats.device_id + '',
      dUuid,
      dailyUsage: stats.Daily_Usage,
      avgDaily: stats.Avg_Daily,
      weeklyUsage: stats.Weekly_Usage,
      avgWeekly: stats.Avg_Weekly,
      monthlyUsage: stats.Monthly_Usage,
      avgMonthly: stats.Avg_Monthly,
      yearlyUsage: stats.Yearly_Usage,
    }
    return agg
  }, {})
}

export async function getWaterUsageCosts(uom: string) {
  const responseCosts = await backend.get(`/water_usage/cost`, {
    params: {
      uom,
    },
  })

  const costs = responseCosts?.data?.usage_data

  const waterUsageCostsArray: WaterUsageCosts[] = costs.map((stats: any) => {
    return {
      deviceId: stats.device_id + '',
      dUuid: stats.d_uuid + '',
      dailyUsageFormatted: formatCurrency(stats.Daily_Usage, stats.Currency),
      avgDailyFormatted: formatCurrency(stats.Avg_Daily, stats.Currency),
      weeklyUsageFormatted: formatCurrency(stats.Weekly_Usage, stats.Currency),
      avgWeeklyFormatted: formatCurrency(stats.Avg_Weekly, stats.Currency),
      monthlyUsageFormatted: formatCurrency(stats.Monthly_Usage, stats.Currency),
      avgMonthlyFormatted: formatCurrency(stats.Avg_Monthly, stats.Currency),
      yearlyUsageFormatted: formatCurrency(stats.Yearly_Usage, stats.Currency),
      dailyUsage: stats.Daily_Usage,
      avgDaily: stats.Avg_Daily,
      weeklyUsage: stats.Weekly_Usage,
      avgWeekly: stats.Avg_Weekly,
      monthlyUsage: stats.Monthly_Usage,
      avgMonthly: stats.Avg_Monthly,
      yearlyUsage: stats.Yearly_Usage,
      currency: stats.Currency,
    }
  })
  return waterUsageCostsArray.reduce(
    (agg: { [key: string]: WaterUsageCosts }, curr) => {
      agg[curr.dUuid] = curr
      return agg
    },
    {},
  )
}

export const filterWaterUsageCosts = (
  selectedDevices: Device[],
  waterUsageCosts: { [key: string]: WaterUsageCosts } | undefined,
) => {
  if (waterUsageCosts) {
    const filteredWaterUsageCosts: WaterUsageStats | {} = Object.keys(waterUsageCosts)
      .filter((dUuid) => {
        const device = selectedDevices.find((d: Device) => d.dUuid === dUuid)
        if (device) {
          return dUuid
        }
      })
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageCosts[key] })
      }, {})

    const mainMetersWaterUsageCosts: WaterUsageStats | {} = Object.keys(filteredWaterUsageCosts)
      .filter((dUuid) => {
        const device = selectedDevices.find(
          (d: Device) => d.dUuid === dUuid && d.deviceSettings.masterDeviceIdRef === null,
        )
        if (device) {
          return dUuid
        }
      })
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageCosts[key] })
      }, {})

    const subMetersWaterUsageCosts: WaterUsageStats | {} = Object.keys(filteredWaterUsageCosts)
      .filter((dUuid) => {
        const device = selectedDevices.find(
          (d: Device) => d.dUuid === dUuid && d.deviceSettings.masterDeviceIdRef !== null,
        )
        if (device) {
          return dUuid
        }
      })
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageCosts[key] })
      }, {})

    return {
      filteredWaterUsageCosts: filteredWaterUsageCosts,
      mainMetersWaterUsageCosts: mainMetersWaterUsageCosts as { [key: string]: WaterUsageStats },
      subMetersWaterUsageCosts: subMetersWaterUsageCosts,
    }
  } else {
    return {
      filteredWaterUsageCosts: {},
      mainMetersWaterUsageCosts: {},
      subMetersWaterUsageCosts: {},
    }
  }
}

export const filterWaterUsageStats = (
  selectedDevices: Device[],
  waterUsageStats: { [key: string]: WaterUsageStats } | undefined,
) => {
  if (waterUsageStats) {
    const filteredWaterUsageStats: WaterUsageStats | {} = Object.keys(waterUsageStats)
      .filter((dUuid: string) => selectedDevices.find((d: Device) => d.dUuid === dUuid))
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageStats[key] })
      }, {})

    const mainMetersWaterUsageStats: WaterUsageStats | {} = Object.keys(filteredWaterUsageStats)
      .filter((dUuid: string) =>
        selectedDevices.find(
          (d: Device) => d.dUuid === dUuid && d.deviceSettings.masterDeviceIdRef === null,
        ),
      )
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageStats[key] })
      }, {})

    const subMetersWaterUsageStats: WaterUsageStats | {} = Object.keys(filteredWaterUsageStats)
      .filter((dUuid: string) =>
        selectedDevices.find(
          (d: Device) => d.dUuid === dUuid && d.deviceSettings.masterDeviceIdRef !== null,
        ),
      )
      .reduce((cur, key) => {
        return Object.assign(cur, { [key]: waterUsageStats[key] })
      }, {})

    return {
      filteredWaterUsageStats: filteredWaterUsageStats as { [key: string]: WaterUsageStats },
      mainMetersWaterUsageStats: mainMetersWaterUsageStats as { [key: string]: WaterUsageStats },
      subMetersWaterUsageStats,
    }
  } else {
    return {
      filteredWaterUsageStats: {},
      mainMetersWaterUsageStats: {},
      subMetersWaterUsageStats: {},
    }
  }
}

export async function getDevicesWaterUsage(
  params: WaterUsageQueryMultipleDevices,
) {
  const multipleDeviceWaterUsage: deviceWaterUsage[][] = []
  try {
    await Promise.all(
      params.deviceIds.map(async (deviceId: string) => {
        const response = await backend.get(`/water_usage/${deviceId}`, {
          params: params,
        })
        multipleDeviceWaterUsage.push(transformDeviceWaterUsageResponse(response.data.usage_data, params, deviceId))
      }),
    )
    return buildMultipleDeviceUsageResult(multipleDeviceWaterUsage)
  } catch (e: any) {
    notifyError('There was an error trying to fetch data, please try again later', {
      id: 'getDevicesWaterUsage',
      autoClose: 5000,
    })
  }
}

export async function getLatestDeviceWaterUsage(
  deviceId: string,
  params: WaterUsageQueryMultipleDevices,
) {
  try {
    const response = await backend.get(`/devices/${deviceId}/daily_flow`, {
      params: {
        req_date: moment(params.startDate).format('YYYY-MM-DD'),
        uom: params.uom,
        idType: params.idType,
      },
    })

    const usage = transformDeviceWaterUsageResponse(response.data.usage_data, params, deviceId)
    return buildMultipleDeviceUsageResult([usage])
  } catch (e: any) {
    notifyError('There was an error trying to refresh device data, please try again later', {
      id: 'getLatestDeviceWaterUsage',
      autoClose: 5000,
    })
  }
}

function transformDeviceWaterUsageResponse(
  usageData: any,
  params: WaterUsageQueryMultipleDevices,
  deviceId: string,
): deviceWaterUsage[] {
  return !usageData.Flow
    ? JSON.parse(JSON.stringify(usageData))
    : (usageData.Flow as any[]).slice(1).map((f, i) => {
      return {
        date: moment(params.startDate)
          .utc()
          .startOf('day')
          .add(i, 'hour')
          .format('YYYY-MM-DDTHH:mm:ss'),
        device_id: deviceId,
        hour: i,
        flow: roundToTwo(f),
      }
    })
}

function buildMultipleDeviceUsageResult(multipleDeviceWaterUsage: deviceWaterUsage[][]) {
  const totalUsageOnDevices = multipleDeviceWaterUsage.map((usage: deviceWaterUsage[]) => {
    return roundToTwo(
      usage.reduce((acc, usage) => {
        if (usage.flow !== null && !isNaN(usage.flow)) {
          return acc + usage.flow
        } else {
          return acc
        }
      }, 0),
    )
  })

  const totalUsageByDevice: Record<string, number> = {}

  multipleDeviceWaterUsage.forEach((usage: deviceWaterUsage[]) => {
    usage.forEach((usage: deviceWaterUsage) => {
      const { device_id, flow } = usage
      if (totalUsageByDevice.hasOwnProperty(device_id)) {
        if (flow !== null && !isNaN(flow)) {
          totalUsageByDevice[device_id] += flow
        }
      } else {
        if (flow !== null && !isNaN(flow)) {
          totalUsageByDevice[device_id] = flow
        } else {
          totalUsageByDevice[device_id] = 0
        }
      }
    })
  })

  const totalUsage = totalUsageOnDevices.reduce((acc, curr) => acc + curr, 0)

  return {
    usage: multipleDeviceWaterUsage,
    totalUsageByDevice,
    totalUsage,
  }
}

const getAlertWaterUsage = async (params: WaterUsageQuery) => {
  try {
    const { deviceId, ...queryParams } = params
    const response = await backend.get(`/water_usage/${params.deviceId}/alert_average`, {
      params: queryParams,
    })

    const usageData = response.data?.usageData
    const alertWaterUsage: { [x: string]: WaterUsage } = {}
    alertWaterUsage[JSON.stringify(params)] = {
      ...params,
      aggregated: {
        [`${Aggregator.CURRENT.toString()}`]: extractFlow(usageData, 'flow'),
        [`${Aggregator.ACTUAL_BASELINE.toString()}`]: extractFlow(usageData, 'average'),
        [`${Aggregator.EXPECTED_BASELINE.toString()}`]: extractFlow(usageData, 'expectedBaseline'),
        [`${Aggregator.ONE_WEEK_PRIOR.toString()}`]: extractFlow(usageData, 'flow7DP'),
        [`${Aggregator.BASELINE_UPPER_LIMIT.toString()}`]: extractFlow(
          usageData,
          'upperFlowLimit',
        ),
        [`${Aggregator.NINETY_DAYS_WX.toString()}`]: extractFlow(usageData, 'averageWExcluded'),
      },
    }
    return alertWaterUsage
  } catch (e) {
    notifyError('There was an error trying to fetch data, please try again later', {
      id: 'getAlertWaterUsage',
      autoClose: 5000,
    })
  }
}

const getAggregatedUsage = async (params: AggUsageQuery): Promise<AggregatedUsageByDevice> => {
  const response = await backend.get(`/water_usage/aggregated/month`, {
    params: params,
  })

  const { usageData } = camelcaseKeys(response.data, { deep: true }) as AggregatedUsageResponse

  return usageData.reduce((acc: AggregatedUsageByDevice, record) => {
    if (!acc[record.dUuid]) {
      acc[record.dUuid] = {}
    }

    if (!acc[record.dUuid][record.year]) {
      acc[record.dUuid][record.year] = {}
    }

    if (!acc[record.dUuid][record.year][record.timeValue]) {
      acc[record.dUuid][record.year][record.timeValue] = record.usage
    }

    return acc
  }, {})
}

const getMonthlyLeakStats = async (params: MonthlyLeakQuery): Promise<MonthlyLeakByDevice> => {
  const response = await backend.get<MonthlyLeakResponse>('/usage_alerts/stats/leak/month', {
    params: params,
  })

  return response.data.data.reduce((acc: MonthlyLeakByDevice, record) => {
    if (!acc[record.dUuid]) {
      acc[record.dUuid] = {}
    }

    if (!acc[record.dUuid][record.year]) {
      acc[record.dUuid][record.year] = {}
    }

    if (!acc[record.dUuid][record.year][record.timeValue]) {
      acc[record.dUuid][record.year][record.timeValue] = record.value
    }
    return acc
  }, {})
}

const filterMonthlyLeakStats = (
  devices: Device[],
  monthlyLeakStats: MonthlyLeakByDevice | undefined,
): MonthlyLeakByDevice => {
  if (!monthlyLeakStats) return {}

  const filteredMonthlyLeakStats: MonthlyLeakByDevice = Object.keys(monthlyLeakStats).reduce(
    (acc: MonthlyLeakByDevice, key) => {
      const d: Device | undefined = devices.find((d: Device) => d.dUuid === key)
      if (d) {
        return Object.assign(acc, { [key]: monthlyLeakStats[key] })
      } else {
        return acc
      }
    },
    {},
  )

  return filteredMonthlyLeakStats
}
